/**
 * 貼文排程設定的 form 內容（不含 Modal 殼，不含按鈕）。
 *
 * 由 PostScheduleModal 與 PostEditModal sub-view 共用：
 * - PostScheduleModal：MyPost 使用，渲染為獨立 modal
 * - PostEditModal：在 modal 內以 sub-view 形式 render，配返回鍵切換
 *
 * 日期時間統一使用共用元件 DatePicker（flatpickr），單一控制項選日期+時間，24 小時制。
 *
 * 確認/取消/移除排程按鈕統一放在呼叫端的 Modal.Footer（全站 modal 慣例），
 * 本元件只負責欄位，透過 ref 暴露 confirm() / clear()，並用 onValidityChange 回報是否可確認。
 */
import { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import DatePicker from '@/components/ui/DatePicker';

const PostScheduleForm = forwardRef(({ initialValues = {}, onConfirm, onValidityChange }, ref) => {
  const { t } = useTranslation();
  const [scheduledAt, setScheduledAt] = useState(null);

  // 掛載當下的時間，是下面 minDate 與 defaultTime 共同的基準。刻意只取樣一次：
  // 兩處各自呼叫 new Date() 的話，剛好跨過午夜時會落在不同天。
  //
  // 全站顯示已統一為本地時間，DatePicker（flatpickr）認的也是本地 Date，
  // 兩者語意一致，因此 state、選擇器、畫面三者可以直接共用同一個 Date，
  // 不再需要任何「牆上時間 ↔ instant」的轉換（原本的 wallToUtcInstant /
  // utcInstantToWall 與傳給 flatpickr 的 now 覆寫都是為了顯示 UTC 而存在，已一併移除）。
  const now = useMemo(() => new Date(), []);

  // 排程只能選未來時間；在 mount 時凍結一次即可，實際送出前仍會用當下時間再驗證一次。
  // minDate 只給到「日」，不能帶時分：flatpickr 拿到帶時分的 minDate 後，會在 setHoursFromInputs
  // 把使用者輸入的時間夾擠回 minDate 的時分（靜默改值），而夾擠時毫秒被歸零，結果反而比 minDate
  // 早幾百毫秒，接著又被 isEnabled 判定不合法整個清空——就是「輸入時間後 input 閃一下變空白」。
  // 時間是否為未來，一律由下面的 isInvalidTime 明確報錯，不靠套件靜默修正
  const minDate = useMemo(
    () => new Date(now.getFullYear(), now.getMonth(), now.getDate()),
    [now],
  );

  // 日曆的預設時分帶「現在」。原本是 minDate 帶時分的副作用（flatpickr getDefaultHours 會把預設值
  // 往上推到 minDate 的時分），minDate 改成整日後改為明確指定，維持原本行為
  const defaultTime = useMemo(
    () => ({ hour: now.getHours(), minute: now.getMinutes() }),
    [now],
  );

  useEffect(() => {
    // initialValues.scheduledAt 由呼叫端以 parseBackendDate 解析後傳入 Date；
    // 非法值一律視為未排程
    const raw = initialValues.scheduledAt;
    const parsed = raw ? new Date(raw) : null;
    setScheduledAt(parsed && !isNaN(parsed.getTime()) ? parsed : null);
    // 依賴 initialValues 物件 ref 變化（呼叫端應在 open 時建立新 obj）
  }, [initialValues]);

  const isInvalidTime = !!scheduledAt && scheduledAt <= new Date();
  const canConfirm = !!scheduledAt && !isInvalidTime;

  useEffect(() => {
    onValidityChange?.(canConfirm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canConfirm]);

  useImperativeHandle(ref, () => ({
    confirm: () => {
      if (!canConfirm) return;
      onConfirm?.({ scheduledAt });
    },
    clear: () => {
      onConfirm?.({ scheduledAt: null });
    },
  }));

  return (
    <div className="mb-3">
      {/* 錯誤訊息放在 input 上方：日曆是往下展開並蓋在 input 下方的，訊息放下面會被日曆整個遮住，
          而關掉日曆才看得到時值又已經被改動，等於永遠看不清楚 */}
      <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
        <Form.Label className="fw-bold mb-0">{t('post_schedule_label')}</Form.Label>
        {isInvalidTime && (
          <small className="text-danger">{t('post_schedule_invalid_time')}</small>
        )}
      </div>

      <DatePicker
        enableTime
        minDate={minDate}
        defaultHour={defaultTime.hour}
        defaultMinute={defaultTime.minute}
        value={scheduledAt}
        onChange={setScheduledAt}
      />
    </div>
  );
});

PostScheduleForm.displayName = 'PostScheduleForm';

export default PostScheduleForm;
